import {
  Component,
  ViewChild,
  TemplateRef,
  AfterViewInit,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  GridDataResult,
  DataStateChangeEvent,
} from '@progress/kendo-angular-grid';
import { GroupDescriptor, SortDescriptor } from '@progress/kendo-data-query';
import {
  ColumnOptions,
  GridState,
  MessageService,
  LoaderService,
  ValidationResultService,
  ColumnGroup,
  GridStateCompositeFilterDescriptor,
} from '@shared';
import { GridRowSubstitutionException, GridRowSubstitutionAnchor } from '../../../models';
import { ReportService } from '../../../services';
import { GlobalEventsService, Tier, TiersService } from '@shared-data';
import { COMBO_TYPES, ComboType, COMPONENT_TYPES, ComponentType, EXCEPTION_TYPES, ExceptionType } from '../../../constants';

@Component({
  selector: 'app-substitutions-exceptions',
  templateUrl: './substitutions-exceptions.component.html',
  styleUrl: './substitutions-exceptions.component.scss',
})
export class SubstitutionsExceptionComponent implements AfterViewInit {
  @ViewChild('conditionalFormattingTemplate', { static: false })
  conditionalFormattingTemplate!: TemplateRef<any>;

  substitutionExceptions: GridDataResult = { data: [], total: 0 };

  columns: ColumnOptions[] = [];

  columnGroups: ColumnGroup[] = [];

  state!: GridState;

  pageSize: number = 10;

  fontSize: number = 0.875;

  initialSort: SortDescriptor[] = [{ field: 'itemName', dir: 'asc' }];

  group: GroupDescriptor[] = [];

  tiers: Tier[] = [];

  selectedTier: Tier = new Tier({ id: '-1', name: 'None' });

  comboTypes: ComboType[] = COMBO_TYPES;

  selectedComboType: ComboType = COMBO_TYPES[0];

  componentTypes: ComponentType[] = COMPONENT_TYPES;

  selectedComponentType: ComponentType = COMPONENT_TYPES[0];

  exceptionTypes: ExceptionType[] = EXCEPTION_TYPES;

  selectedExceptionType: ExceptionType = EXCEPTION_TYPES[0];

  anchorGridData: GridDataResult = { data: [], total: 0 };

  anchorColumns: ColumnOptions[] = [];

  anchorState!: GridState;

  // Legend for color-coded pricing
  pricingLegend = [
    { label: 'Pricing Under', color: '#f8d7da', description: 'Current price is lower than default' },
    { label: 'Pricing Exact Difference', color: 'transparent', description: 'Shows the price difference' },
    { label: 'Pricing Over', color: '#fff3cd', description: 'Current price is higher than default' }
  ];

  constructor(
    private reportService: ReportService,
    private messageService: MessageService,
    private loaderService: LoaderService,
    private tierService: TiersService,
    private route: ActivatedRoute,
    private validationErrorService: ValidationResultService,
    private globalEventsService: GlobalEventsService,
  ) {
    const entity = new GridRowSubstitutionException();
    this.columns = entity.getColumns();
    this.columnGroups = entity.getColumnGroups(this.columns);

    // Initialize anchor grid columns using the model
    const anchorEntity = new GridRowSubstitutionAnchor();
    this.anchorColumns = anchorEntity.getColumns();

    this.anchorState = new GridState(1000, []);
  }

  getTiers() {
    this.loaderService.show();

    this.tierService.getTiers().subscribe({
      next: (tiers) => {
        this.tiers = tiers;
        this.selectedTier = this.tiers[0];
        this.setDefaultState();
        this.loaderService.hide();
      },
      error: (error) => {
        this.loaderService.hide();
        this.messageService.error(`Failed to get tiers`);
        var validationError = error?.error;
        if (validationError) {
          this.validationErrorService.show(validationError);
        }
      },
    });
  }

  ngOnInit() {
    this.getTiers();
  }

  ngAfterViewInit() {
    // Assign the conditional formatting template to the component pricing columns
    const componentPriceColumn = this.columns.find(col => col.field === 'componentPrice');
    const componentPriceDeliveryColumn = this.columns.find(col => col.field === 'componentPriceDelivery');

    if (componentPriceColumn) {
      componentPriceColumn.columnTemplate = this.conditionalFormattingTemplate;
    }
    if (componentPriceDeliveryColumn) {
      componentPriceDeliveryColumn.columnTemplate = this.conditionalFormattingTemplate;
    }
  }

  setDefaultState(): void {
    this.state = new GridState(this.pageSize, this.initialSort);

    this.fetchReportData();
    this.fetchAnchorItems();
  }

  applyFilters(): void {
    // Apply tier filter
    if (this.selectedTier.id !== '-1') {
      const tierFilter = {
        field: 'tierId',
        operator: 'eq',
        value: this.selectedTier?.id,
      };

      if (!this.state.filter) {
        this.state.filter = new GridStateCompositeFilterDescriptor().and(tierFilter);
      } else {
        this.state.filter = new GridStateCompositeFilterDescriptor(this.state.filter).and(tierFilter);
      }
    }

    // Apply combo type filter
    if (this.selectedComboType.id !== '-1') {
      const comboTypeFilter = {
        field: 'comboName',
        operator: 'eq',
        value: this.selectedComboType.id,
      };

      if (!this.state.filter) {
        this.state.filter = new GridStateCompositeFilterDescriptor().and(comboTypeFilter);
      } else {
        this.state.filter = new GridStateCompositeFilterDescriptor(this.state.filter).and(comboTypeFilter);
      }
    }

    // Apply component type filter (Drink or Side)
    if (this.selectedComponentType) {
      const componentTypeFilter = {
        field: 'componentType',
        operator: 'eq',
        value: this.selectedComponentType.id,
      };

      if (!this.state.filter) {
        this.state.filter = new GridStateCompositeFilterDescriptor().and(componentTypeFilter);
      } else {
        this.state.filter = new GridStateCompositeFilterDescriptor(this.state.filter).and(componentTypeFilter);
      }
    }

    // Apply exception type filter (Under, Over, or All)
    if (this.selectedExceptionType && this.selectedExceptionType.id) {
      // Map exception type to appropriate boolean field
      const field = this.selectedExceptionType.id === 'Under' ? 'isUnderPriced' : 'isOverPriced';
      const exceptionTypeFilter = {
        field: field,
        operator: 'eq',
        value: true,
      };

      if (!this.state.filter) {
        this.state.filter = new GridStateCompositeFilterDescriptor().and(exceptionTypeFilter);
      } else {
        this.state.filter = new GridStateCompositeFilterDescriptor(this.state.filter).and(exceptionTypeFilter);
      }
    }
  }

  fetchReportData() {
    this.loaderService.show();
    if (!this.state.sort?.length) {
      this.state.sort = this.initialSort;
    }

    this.state.group = this.group;

    this.applyFilters();

    this.reportService.getReportData(`substitutions`, this.state).subscribe({
      next: (data) => {
        this.substitutionExceptions = data;
        this.substitutionExceptions.data = this.substitutionExceptions.data.map(
          (item) =>
            new GridRowSubstitutionException(
              item
            ),
        );

        this.loaderService.hide();
      },
      error: (error) => {
        this.loaderService.hide();
        this.messageService.error(`Failed to load substitution exceptions`);
        var validationError = error?.error;
        if (validationError) {
          this.validationErrorService.show(validationError);
        }
      },
    });
  }

  onDataStateChanged(event: DataStateChangeEvent): void {
    this.state = event as GridState;

    this.fetchReportData();
  }

  onFontSizeChange(size: number): void {
    this.fontSize = size;
  }

  exportExceptions() {
    this.loaderService.show();
    this.reportService.exportExceptions(`substitutions`).subscribe({
      next: (data: Blob) => {
        const blob = new Blob([data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Substitution_Exceptions_${new Date().toDateString()}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.loaderService.hide();
      },
      error: (error) => {
        this.loaderService.hide();
        this.messageService.error(`Failed to export substitution exceptions`);
        var validationError = error?.error;
        if (validationError) {
          this.validationErrorService.show(validationError);
        }
      },
    });
  }

  fetchAnchorItems() {
    if (this.selectedTier.id === '-1' || !this.selectedTier.kingRestaurantId) {
      this.anchorGridData = { data: [], total: 0 };
      return;
    }

    this.loaderService.show();
    this.reportService
      .getSubstitutionAnchors(parseInt(this.selectedTier.kingRestaurantId))
      .subscribe({
        next: (data) => {
          // Apply client-side filtering
          let filtered = data.data;

          // Filter by combo type if selected
          if (this.selectedComboType.id !== '-1') {
            filtered = filtered.filter((item) => item.comboName?.trim() === this.selectedComboType.id.trim());
          }

          // Filter by component type (Drink or Side)
          if (this.selectedComponentType.id) {
            filtered = filtered.filter((item) => item.componentType?.trim() === this.selectedComponentType.id.trim());
          }

          this.anchorGridData = {
            data: filtered.map((item) => new GridRowSubstitutionAnchor(item)),
            total: filtered.length
          };
          this.loaderService.hide();
        },
        error: (error) => {
          this.loaderService.hide();
          this.messageService.error(`Failed to load substitution anchors`);
          var validationError = error?.error;
          if (validationError) {
            this.validationErrorService.show(validationError);
          }
        },
      });
  }

  getCellBackgroundColor(row: any, field: string): string {
    // Map current component price fields to their corresponding default price fields
    const fieldMapping: { [key: string]: string } = {
      componentPrice: 'subExactDiff',
      componentPriceDelivery: 'subDeliveryExactDiff',
    };

    const defaultField = fieldMapping[field];
    if (!defaultField) {
      return 'transparent';
    }

    const currentValue = row[field];
    const defaultValue = row[defaultField];

    // Handle null/undefined values
    if (currentValue == null || defaultValue == null) {
      return 'transparent';
    }

    // Compare values and return appropriate background color
    if (currentValue > defaultValue) {
      return '#fff3cd'; // Yellow for higher prices
    } else if (currentValue < defaultValue) {
      return '#f8d7da'; // Red for lower prices
    }

    return 'transparent'; // No formatting if equal
  }

  fixSubstitutionExceptions() {
    this.loaderService.show();

    this.reportService
      .fixSubstitutionExceptions(
        this.selectedTier.id,
        this.state,
      )
      .subscribe({
        next: () => {
          this.messageService
            .success(`Tier ${this.selectedTier.name} substitution exceptions fixed successfully,
           please sync changes`);
          this.fetchReportData();
          this.globalEventsService.updatePendingChanges();
          this.loaderService.hide();
        },
        error: (error) => {
          this.messageService.error(
            `Failed to fix tier ${this.selectedTier.name} substitution exceptions`,
          );
          var validationError = error?.error;
          if (validationError) {
            this.validationErrorService.show(validationError);
          }
          this.loaderService.hide();
        },
      });
  }
}
